import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, AlertTriangle, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { translateSupabaseError } from '@/utils/errorTranslation';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';
import { useQueryClient } from '@tanstack/react-query';
import { clearRecoveryLock, getLinkError, hasUnexchangedCode, isRecoveryLocked } from '@/lib/recoveryLock';

interface LinkError {
  title: string;
  message: string;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [linkError, setLinkError] = useState<LinkError | null>(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isRecoverySession, setIsRecoverySession] = useState(false);
  const queryClient = useQueryClient();

  // Password requirements validation
  const passwordChecks = {
    minLength: password.length >= 6,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const allRequirementsMet = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // AUTH-CADASTRO Ato 1 (pacote, seção 3): validação na ordem do contrato
  useEffect(() => {
    let cancelled = false;

    const expiredError: LinkError = {
      title: 'Link Expirado',
      message:
        'O link de recuperação expirou, já foi usado ou foi aberto em outro navegador. Por segurança, ele vale uma vez só e no mesmo navegador em que você pediu a recuperação. Solicite um novo link.',
    };
    const invalidSessionError: LinkError = {
      title: 'Sessão Inválida',
      message: 'Não foi possível validar seu link de recuperação. Solicite um novo link.',
    };

    const validate = async () => {
      // getSession espera a troca do código pelo cliente (PKCE); limite de 5 s
      const result = await Promise.race([
        supabase.auth.getSession().then(({ data }) => ({ session: data.session, timedOut: false })),
        new Promise<{ session: null; timedOut: true }>((resolve) =>
          setTimeout(() => resolve({ session: null, timedOut: true }), 5000)
        ),
      ]);
      if (cancelled) return;

      const { session, timedOut } = result;

      if (session && isRecoveryLocked()) {
        setIsRecoverySession(true);
      } else if (getLinkError()) {
        setLinkError(expiredError);
      } else if (hasUnexchangedCode()) {
        setLinkError(expiredError);
      } else if (session) {
        setIsRecoverySession(false); // troca de senha voluntária, sem trava
      } else {
        setLinkError(invalidSessionError);
      }

      // A trava só vale com sessão; no tempo esgotado a troca pode ainda chegar, então a marca fica
      // (o AuthContext a limpa se a inicialização terminar sem sessão)
      if (!session && !timedOut) clearRecoveryLock();
      setIsCheckingSession(false);
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLeave = async () => {
    // "Sair" segue o logout padrão do sistema (global, como AuthContext.signOut)
    clearRecoveryLock();
    await supabase.auth.signOut();
    queryClient.clear();
    navigate('/auth?tab=signin', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!allRequirementsMet) {
      toast({
        title: "Erro",
        description: "A senha não atende todos os requisitos",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        toast({
          title: "Erro ao redefinir senha",
          description: translateSupabaseError(error),
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      toast({
        title: "Senha redefinida!",
        description: "Sua senha foi alterada com sucesso. Faça login com a nova senha.",
      });

      // AUTH-CADASTRO Ato 1 (decisão 4 e pergunta 2): sai da conta e derruba as outras sessões do usuário
      clearRecoveryLock();
      await supabase.auth.signOut({ scope: 'global' });
      queryClient.clear();
      navigate('/auth?tab=signin&senha=alterada', { replace: true });

    } catch (error: any) {
      toast({
        title: "Erro",
        description: translateSupabaseError(error),
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <li className={`flex items-center gap-1.5 text-xs ${met ? 'text-green-600' : 'text-muted-foreground'}`}>
      {met ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : (
        <XCircle className="h-3 w-3" />
      )}
      {text}
    </li>
  );

  // Show loading state while checking session
  if (isCheckingSession) {
    return (
      <div className="min-h-screen bg-gradient-industrial flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/gonx-logo.png" 
              alt="GOnX Logo" 
              className="h-72 w-auto"
            />
          </div>
          <p className="text-muted-foreground">Validando link de recuperação...</p>
        </div>
      </div>
    );
  }

  // Show error state if link is expired/invalid
  if (linkError) {
    return (
      <div className="min-h-screen bg-gradient-industrial flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/gonx-logo.png" 
                alt="GOnX Logo" 
                className="h-72 w-auto"
              />
            </div>
          </div>

          <Card className="shadow-lg border-destructive/50">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                <AlertTriangle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle className="text-xl text-destructive">{linkError.title}</CardTitle>
              <CardDescription className="text-base">
                {linkError.message}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full"
                onClick={() => setShowForgotPassword(true)}
              >
                <Mail className="mr-2 h-4 w-4" />
                Solicitar Novo Link
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/auth?tab=signin')}
              >
                Voltar para Login
              </Button>
            </CardContent>
          </Card>

          <div className="text-center mt-6 text-sm text-muted-foreground">
            <p>© 2024 GOnX - Softwares de Gestão</p>
          </div>
        </div>

        <ForgotPasswordDialog 
          open={showForgotPassword} 
          onOpenChange={setShowForgotPassword} 
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-industrial flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/gonx-logo.png" 
              alt="GOnX Logo" 
              className="h-72 w-auto"
            />
          </div>
        </div>

        {/* Reset Password Card */}
        <Card className="shadow-lg border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Redefinir Senha</CardTitle>
            <CardDescription>
              Digite sua nova senha
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                </div>
                
                {/* Password requirements */}
                <div className="bg-muted/50 rounded-md p-3 mt-2">
                  <p className="text-xs font-medium mb-2 text-muted-foreground">Requisitos da senha:</p>
                  <ul className="space-y-1">
                    <RequirementItem met={passwordChecks.minLength} text="Mínimo de 6 caracteres" />
                    <RequirementItem met={passwordChecks.hasUppercase} text="Uma letra maiúscula" />
                    <RequirementItem met={passwordChecks.hasLowercase} text="Uma letra minúscula" />
                    <RequirementItem met={passwordChecks.hasNumber} text="Um número" />
                    <RequirementItem met={passwordChecks.hasSpecial} text="Um caractere especial (!@#$%^&*)" />
                  </ul>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                </div>
                {confirmPassword && (
                  <p className={`text-xs flex items-center gap-1 ${passwordsMatch ? 'text-green-600' : 'text-destructive'}`}>
                    {passwordsMatch ? (
                      <>
                        <CheckCircle2 className="h-3 w-3" />
                        Senhas coincidem
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3 w-3" />
                        Senhas não coincidem
                      </>
                    )}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || !allRequirementsMet || !passwordsMatch}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar Nova Senha'}
              </Button>
              {isRecoverySession && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleLeave}
                  disabled={isSubmitting}
                >
                  Sair
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>© 2024 GOnX - Softwares de Gestão</p>
        </div>
      </div>
    </div>
  );
}
